import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { TaskPageHeader, TaskPageContent } from '../../components/task'
import { Task, TaskFilters, TaskMutationPayload, TaskStatus } from '../../model/Task'
import { SORT_DIRECTION } from '../../model/Resource'
import { isDefined } from '../../misc/functions.utilities'
import { DeleteValidationModal } from '../../components/shared'
import { formatISO } from 'date-fns'
import { NavBar } from '../../components/layout'
import {
  useCreateTask,
  useDeleteAllDoneTasks,
  useDeleteTask,
  useGetTaskCount,
  useGetTasks,
  useReadTask,
  useUpdateTask,
} from '../../hooks/queries/task'

export const TaskPage: FunctionComponent = () => {
  const [searchParams] = useSearchParams()
  const [newTask, setNewTask] = useState<Partial<Task>>()
  const [toBeDeletedTaskId, setToBeDeletedTaskId] = useState<number>()
  const [deleteAllDoneTasksModal, setDeleteAllDoneTasksModal] = useState<boolean>(false)
  const [hasDocumentFilterDelete, setHasDocumentFilterDelete] = useState<boolean>(false)

  const filters = useMemo<TaskFilters>(
    () => ({
      title: searchParams.get('title') || undefined,
      type: searchParams.get('types') || undefined,
      patientId: searchParams.get('patientId')
        ? parseInt(searchParams.get('patientId')!, 10)
        : undefined,
      startDate: searchParams.get('startDate')
        ? formatISO(new Date(searchParams.get('startDate')!))
        : undefined,
      endDate: searchParams.get('endDate')
        ? formatISO(new Date(searchParams.get('endDate')!))
        : undefined,
      order: (searchParams.get('order') as TaskFilters['order']) ?? SORT_DIRECTION.DESC,
      orderBy: (searchParams.get('orderBy') as TaskFilters['orderBy']) ?? 'date',
      autoCreated: searchParams.get('hideAutoCreated') === 'true' ? false : undefined,
      assignedToMe: searchParams.get('assignedToMe') === 'true' ? false : undefined,
    }),
    [searchParams],
  )

  const { mutate: createTask } = useCreateTask()
  const { mutate: updateTask } = useUpdateTask()
  const { mutate: deleteTask } = useDeleteTask()
  const { mutate: deleteAllDoneTasks } = useDeleteAllDoneTasks()
  const { mutate: readTasks } = useReadTask()
  const { data: totalNotificationTaskUnread } = useGetTaskCount(
    { read: false },
    { infinityStale: true },
  )
  const {
    taskList,
    query: { isLoading: isListLoading },
  } = useGetTasks({ filters })

  const tasksTodo = useMemo(
    () => taskList.filter((taskTodo) => taskTodo.status === TaskStatus.TODO && !taskTodo.document),
    [taskList],
  )

  const tasksDocument = useMemo(
    () =>
      taskList.filter(
        (taskDocument) => taskDocument.status === TaskStatus.TODO && taskDocument.document,
      ),
    [taskList],
  )

  const tasksDone = useMemo(
    () => taskList.filter((taskDone) => taskDone.status === TaskStatus.DONE),
    [taskList],
  )

  const handleNewTask = useCallback(() => {
    setNewTask({ title: '', status: TaskStatus.TODO, createdAt: new Date(), editMode: true })
  }, [])

  const emptyNewTask = useCallback(() => {
    setNewTask(undefined)
  }, [])

  const handleCreateOrUpdateTask = useCallback(
    (task: TaskMutationPayload) => {
      if (task.id) {
        updateTask({ taskId: task.id, variables: { ...task, autoCreated: false } })
      } else if (task.title) {
        createTask(task)
        emptyNewTask()
      } else {
        emptyNewTask()
      }
    },
    [updateTask, createTask, emptyNewTask],
  )

  const handleConfirmDeleteTask = useCallback(() => {
    if (isDefined(toBeDeletedTaskId)) {
      deleteTask({ taskId: toBeDeletedTaskId })
    } else {
      emptyNewTask()
    }
    setToBeDeletedTaskId(undefined)
  }, [deleteTask, emptyNewTask, toBeDeletedTaskId])

  const handleConfirmDeleteAllDoneTasks = useCallback(() => {
    deleteAllDoneTasks(hasDocumentFilterDelete)
    setDeleteAllDoneTasksModal(false)
    setHasDocumentFilterDelete(false)
  }, [deleteAllDoneTasks, hasDocumentFilterDelete])

  useEffect(() => {
    if (totalNotificationTaskUnread) {
      readTasks()
    }
  }, [readTasks, totalNotificationTaskUnread])

  const todoWithNewTask: Partial<Task>[] = useMemo(
    () => (newTask ? [newTask, ...tasksTodo] : tasksTodo),
    [newTask, tasksTodo],
  )

  return (
    <>
      <NavBar>
        <TaskPageHeader
          itemsCount={taskList.length}
          newTask={!newTask ? handleNewTask : undefined}
          testId="task-page-header"
        />
      </NavBar>
      <TaskPageContent
        tasksDone={tasksDone}
        onDeleteTask={setToBeDeletedTaskId}
        tasksTodo={todoWithNewTask}
        tasksDocument={tasksDocument}
        onCreateOrUpdate={handleCreateOrUpdateTask}
        newTask={!newTask ? handleNewTask : undefined}
        loading={isListLoading}
        removeNewTask={emptyNewTask}
        onDeleteAllDoneTasks={(hasDocument) => {
          setHasDocumentFilterDelete(hasDocument ?? false)
          setDeleteAllDoneTasksModal(true)
        }}
        testId="task-page-content"
      />
      <DeleteValidationModal
        display={!!toBeDeletedTaskId}
        title="Voulez-vous vraiment supprimer cette tâche ?"
        onClose={() => setToBeDeletedTaskId(undefined)}
        onSubmit={handleConfirmDeleteTask}
        testId="modal-tasks_page-task-delete"
      />
      <DeleteValidationModal
        display={deleteAllDoneTasksModal}
        title="Voulez-vous vraiment effacer vos tâches terminées ?"
        onClose={() => setDeleteAllDoneTasksModal(false)}
        onSubmit={handleConfirmDeleteAllDoneTasks}
        testId="modal-tasks_page-completed_tasks-delete"
      />
    </>
  )
}
